import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Globe, ExternalLink, Plus, Search, Filter,
  GraduationCap, Building2, MapPin, Briefcase, RefreshCw, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";

export default function JobSourcesPage() {
  const { user } = useAuth();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [scraping, setScraping] = useState(false);
  const [scrapeStats, setScrapeStats] = useState(null);
  
  // Add source form
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newSourceUrl, setNewSourceUrl] = useState("");
  const [newSourceName, setNewSourceName] = useState("");
  const [newSourceDesc, setNewSourceDesc] = useState("");
  const [scrapeNow, setScrapeNow] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSources();
    fetchScrapeStats();
  }, []);
  
  const fetchSources = async () => {
    try {
      const response = await fetch(`${API}/websites/approved`);
      if (response.ok) {
        const data = await response.json();
        setSources(data);
      }
    } catch (error) {
      console.error("Error fetching sources:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchScrapeStats = async () => {
    try {
      const response = await fetch(`${API}/jobs/scrape/status`);
      if (response.ok) {
        setScrapeStats(await response.json());
      }
    } catch (error) {
      console.error("Error fetching scrape stats:", error);
    }
  };
  
  const handleScrapeAll = async () => {
    if (!user) {
      toast.error("Please login to fetch jobs");
      return;
    }
    
    setScraping(true);
    try {
      const response = await fetch(`${API}/jobs/scrape`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Fetched ${result.total_new} new jobs from ${result.sources_scraped?.length || 0} sources!`);
        fetchScrapeStats();
      } else {
        toast.error("Failed to fetch jobs");
      }
    } catch (error) {
      toast.error("Error fetching jobs");
    } finally {
      setScraping(false);
    }
  };
  
  const handleAddSource = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please login to add sources");
      return;
    }
    
    if (!newSourceUrl || !newSourceName) {
      toast.error("Please provide URL and name");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/sources/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          url: newSourceUrl,
          name: newSourceName,
          description: newSourceDesc,
          scrape_now: scrapeNow
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.scrape_result?.jobs_found > 0) {
          toast.success(`Source added! Found ${result.scrape_result.jobs_found} jobs.`);
        } else {
          toast.success("Source submitted for review!");
        }
        setShowAddDialog(false);
        setNewSourceUrl("");
        setNewSourceName("");
        setNewSourceDesc("");
        fetchScrapeStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add source");
      }
    } catch (error) {
      toast.error("Error adding source");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredSources = sources.filter(source => 
    source.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    source.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Categorize sources
  const majorBoards = filteredSources.filter(s => 
    ['Academic Jobs Online', 'Nature Careers', 'Science Careers (AAAS)', 'Times Higher Education Jobs', 'HigherEdJobs'].includes(s.name)
  );
  const regional = filteredSources.filter(s => 
    ['EURAXESS', 'Jobs.ac.uk', 'JREC-IN (Japan)', 'Academics.com'].includes(s.name)
  );
  const fieldSpecific = filteredSources.filter(s => 
    ['MathJobs', 'Physics Today Jobs', 'ACS Careers', 'EcoEvoJobs', 'EconJobMarket'].includes(s.name)
  );
  const other = filteredSources.filter(s => 
    !majorBoards.includes(s) && !regional.includes(s) && !fieldSpecific.includes(s)
  );

  const SourceCard = ({ source, index }) => (
    <motion.a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="block p-5 rounded-sm bg-[#0A0A0A] border border-white/10 hover:border-[#D4AF37]/50 transition-all group"
      data-testid={`source-card-${source.source_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white group-hover:text-[#D4AF37] transition-colors mb-1">
            {source.name}
          </h3>
          <p className="text-sm text-gray-500 line-clamp-2">
            {source.description}
          </p>
        </div>
        <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-[#D4AF37] flex-shrink-0 mt-1" />
      </div>
    </motion.a>
  );

  const SourceSection = ({ title, icon: Icon, sources, color }) => (
    sources.length > 0 && (
      <section className="mb-10">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-white mb-4">
          <div className={`w-8 h-8 rounded-sm ${color} flex items-center justify-center`}>
            <Icon className="w-4 h-4" />
          </div>
          {title}
          <span className="text-sm font-normal text-gray-500">({sources.length})</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sources.map((source, index) => (
            <SourceCard key={source.source_id} source={source} index={index} />
          ))}
        </div>
      </section>
    )
  );

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="job-sources-page">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-medium text-white mb-2">
              Job Sources
            </h1>
            <p className="text-gray-400">
              {sources.length} job websites • {scrapeStats?.total_jobs || 0} total jobs in database
            </p>
          </div>
          
          {/* Stats & Actions */}
          <div className="flex flex-wrap gap-3">
            {user && (
              <>
                <Button 
                  onClick={handleScrapeAll}
                  disabled={scraping}
                  variant="outline"
                  className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm"
                  data-testid="fetch-jobs-btn"
                >
                  {scraping ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Fetch New Jobs
                    </>
                  )}
                </Button>
                
                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-bold rounded-sm"
                      data-testid="add-source-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Source
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0A0A0A] border-white/10 sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-white">Add a Job Source</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddSource} className="space-y-4 mt-4">
                      <div>
                        <Label className="text-gray-300">Website URL *</Label>
                        <Input
                          type="url"
                          placeholder="https://example.com/jobs"
                          value={newSourceUrl}
                          onChange={(e) => setNewSourceUrl(e.target.value)}
                          required
                          className="mt-2 bg-white/5 border-white/10 text-white"
                          data-testid="add-source-url-input"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Source Name *</Label>
                        <Input
                          placeholder="e.g., Nature Careers"
                          value={newSourceName}
                          onChange={(e) => setNewSourceName(e.target.value)}
                          required
                          className="mt-2 bg-white/5 border-white/10 text-white"
                          data-testid="add-source-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Description</Label>
                        <Textarea
                          placeholder="Brief description..."
                          value={newSourceDesc}
                          onChange={(e) => setNewSourceDesc(e.target.value)}
                          className="mt-2 bg-white/5 border-white/10 text-white min-h-[60px]"
                        />
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <Label className="text-gray-300">Try fetching jobs now</Label>
                        <Switch
                          checked={scrapeNow}
                          onCheckedChange={setScrapeNow}
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#D4AF37] text-black hover:bg-[#C5A028] font-bold rounded-sm"
                        data-testid="submit-source-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          "Add Source"
                        )}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <Input
            type="text"
            placeholder="Search job sources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#D4AF37] rounded-sm max-w-md"
            data-testid="sources-search-input"
          />
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(9)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : filteredSources.length === 0 ? (
          <div className="text-center py-16">
            <Globe className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No sources found matching "{searchQuery}"</p>
          </div>
        ) : searchQuery ? (
          // Show flat list when searching
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSources.map((source, index) => (
              <SourceCard key={source.source_id} source={source} index={index} />
            ))}
          </div>
        ) : (
          // Show categorized when not searching
          <>
            <SourceSection 
              title="Major Academic Job Boards" 
              icon={GraduationCap} 
              sources={majorBoards}
              color="bg-[#800020]/30 text-[#D4AF37]"
            />
            <SourceSection 
              title="Regional Job Portals" 
              icon={MapPin} 
              sources={regional}
              color="bg-blue-500/20 text-blue-400"
            />
            <SourceSection 
              title="Field-Specific Boards" 
              icon={Briefcase} 
              sources={fieldSpecific}
              color="bg-purple-500/20 text-purple-400"
            />
            <SourceSection 
              title="Additional Resources" 
              icon={Globe} 
              sources={other}
              color="bg-green-500/20 text-green-400"
            />
          </>
        )}

        {/* CTA for suggesting */}
        {!user && (
          <div className="mt-12 p-8 rounded-sm bg-[#800020]/10 border border-[#800020]/20 text-center">
            <h3 className="font-serif text-xl text-white mb-2">Know a great job source?</h3>
            <p className="text-gray-400 mb-4">Sign in to suggest websites that should be added to our list.</p>
            <Link to="/register">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-bold rounded-sm">
                Create Account
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
